import { useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { usePermissions } from "contexts/PermissionsContext"
import { useProps } from "contexts/PropsContext"

import { PERMISSIONS } from "constants/permissions"

import { AdminTokenService } from "services/token/AdminTokenService"
import { TokenUserHolding } from "services/token/type"

import { compareDecimalStrings, formatDecimalString, isPositiveDecimal, tokenErrorCopy, Translate, useIdempotencyKey } from "../helpers/token.helpers"
import { useTokenGate } from "../helpers/useTokenGate"
import { useTokenIdentity } from "../helpers/useTokenIdentity"

import { ReasonModal } from "./shared/ReasonModal"
import { TokenField } from "./shared/TokenField"

/**
 * One player's $TOKEN holding on the admin user page, plus the adjust action.
 *
 * 🔴 THIS IS DELIBERATELY NOT A FIELD IN `ModalEditBalance`. That modal sets
 * ABSOLUTE values (`USD: 100` means "make the balance 100") through
 * `Yup.number()` — a JS float. `AdminAdjustBalance` takes a SIGNED DELTA on a
 * `Decimal(38,18)`. Putting the two in one form with equal visual weight means
 * an operator typing `100` gets "mint 100 tokens" where the field beside it
 * would have meant "set to 100", and a float would quietly round the 18th
 * decimal place besides. The semantics are different enough that they get
 * different controls.
 *
 * The card is the discoverable half (an operator looking for a balance finds it
 * where balances live); the modal keeps the destructive half behind a mandatory
 * reason and its own confirmation.
 *
 * Renders NOTHING when the tenant has no $TOKEN (`TOKEN_DISABLED`) or the
 * operator lacks `PAGE_TOKEN`. A tenant whose wallet the monolith still owns
 * must not show a control that can only fail.
 *
 * The figures come with `PAGE_TOKEN`; the Adjust button needs `TOKEN_MANUAL_OPS`
 * on top, because it is the same mint/burn operation as the Manual ops tab and
 * must not be reachable at a lower privilege just because it sits on the user
 * page.
 */
export const TokenUserBalanceCard = ({
  tenant,
  userId,
  onAdjusted
}: {
  tenant: string
  userId: string
  /** Lets the page refresh anything else that shows $TOKEN. */
  onAdjusted?: () => void
}) => {
  const { t } = useTranslation("translation", { useSuspense: false }) as { t: Translate }
  const { darkMode } = useProps()
  const { has } = usePermissions()
  const canSeeToken = has(PERMISSIONS.PAGE_TOKEN)
  const canAdjust = has(PERMISSIONS.TOKEN_MANUAL_OPS)

  const { gate, run } = useTokenGate()
  const { ticker } = useTokenIdentity(tenant)
  const [holding, setHolding] = useState<TokenUserHolding | undefined>()

  const [open, setOpen] = useState(false)
  const [direction, setDirection] = useState<"add" | "remove">("add")
  const [amount, setAmount] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | undefined>()
  const { key: opKey, reset: resetOpKey } = useIdempotencyKey()

  const load = useCallback(async () => {
    if (!canSeeToken || !userId) return
    const data = await run(() => AdminTokenService.getUserHolding(tenant, userId))
    setHolding(data)
  }, [canSeeToken, run, tenant, userId])

  useEffect(() => {
    void load()
  }, [load])

  /**
   * The field takes a POSITIVE magnitude; direction comes from the toggle.
   *
   * 🔴 The wire format is a signed decimal, and this used to be the input format
   * too — "-5" removed five. That made removal invisible: nothing on screen said
   * a minus sign was accepted, so the control read as add-only. Direction is now
   * a choice the operator makes explicitly, and a typed sign is REJECTED rather
   * than honoured, because "-5" in Remove mode would otherwise negate twice and
   * silently add.
   */
  const typedSign = /[+-]/.test(amount)
  const amountWellFormed = !typedSign && isPositiveDecimal(amount)

  // Only the UNSTAKED balance can be removed; staked tokens live in a position.
  // Checked here purely to fail fast — token-service refuses it regardless, and
  // its answer is the authoritative one.
  const balance = holding?.token_balance
  const overdrawing = direction === "remove" && amountWellFormed && !!balance && compareDecimalStrings(amount.trim(), balance) > 0

  const amountInvalid = !amountWellFormed || overdrawing

  const amountHint = typedSign
    ? t("token.hintTypedSign")
    : !amountWellFormed
    ? t("token.hintMalformed")
    : overdrawing
    ? t("token.hintOverdrawing", { available: formatDecimalString(balance, 6) })
    : direction === "remove"
    ? t("token.hintRemovingFrom", { available: formatDecimalString(balance, 6) })
    : t("token.hintAddingTo", { available: formatDecimalString(balance, 6) })

  const start = () => {
    setError(undefined)
    setDirection("add")
    setAmount("")
    // A NEW intent gets a NEW key; retries of this same intent reuse it.
    resetOpKey()
    setOpen(true)
  }

  const submit = async (reason: string) => {
    setSubmitting(true)
    setError(undefined)

    // The signed wire value is assembled HERE, from the toggle — the operator
    // never types a sign.
    const signed = direction === "remove" ? `-${amount.trim()}` : amount.trim()
    const res = await AdminTokenService.adjustBalance(tenant, reason, userId, signed, opKey)
    setSubmitting(false)

    if (!res.ok) {
      // Kept in the dialog, not fired as a toast: INSUFFICIENT_TOKEN is a rule
      // the operator has to read, not a transient error to dismiss.
      setError(tokenErrorCopy(t, res.code, res.message))
      return
    }

    toast.success(t("token.adjustedToastTicker", { balance: formatDecimalString(res.data?.new_token_balance, 6), ticker }))
    setOpen(false)
    await load()
    onAdjusted?.()
  }

  // Not permitted, or this tenant has no $TOKEN at all — show nothing rather
  // than an empty card that implies the player holds zero.
  if (!canSeeToken || gate === "disabled") return null

  // Restates the direction at the point of commit — the confirm button is the
  // last thing read before tokens move.
  const confirmLabel =
    direction === "remove"
      ? t("token.confirmRemove", { amount: amount.trim() || "…", ticker })
      : t("token.confirmAdd", { amount: amount.trim() || "…", ticker })

  return (
    <>
      <div className="flex flex-col gap-3 p-4 w-full rounded-[8px] border border-[rgba(0,0,0,0.1)] dark:border-transparent bg-[rgba(212,212,212,0.1)] dark:bg-[rgba(255,255,255,0.04)] backdrop-blur-md">
        <div className="flex justify-between gap-4">
          <div className="text-[rgba(106,103,105,1)] dark:text-[rgba(156,163,175,1)] text-base">{t("token.userBalanceLabel", { ticker })}</div>
          {canAdjust && (
            <button
              className="text-sm underline text-[rgba(188,43,228,1)] disabled:opacity-40 disabled:no-underline"
              onClick={start}
              disabled={gate === "unavailable"}
            >
              {t("token.adjust")}
            </button>
          )}
        </div>

        <div className="text-2xl text-[rgba(76,73,75,1)] dark:text-white">
          {gate === "unavailable" ? "—" : `${formatDecimalString(holding?.token_balance, 6)} ${ticker}`}
        </div>

        {gate === "unavailable" && (
          <div className="text-sm text-[rgba(239,68,68,1)]">{t("token.serviceUnreachable")}</div>
        )}

        <div className="flex justify-between items-center">
          <div className="text-[rgba(106,103,105,1)] dark:text-[rgba(156,163,175,1)] text-base">{t("token.userStaked")}</div>
          <div className="text-[rgba(59,130,246,1)] text-base">
            {gate === "unavailable" ? "—" : formatDecimalString(holding?.total_staked, 6)}
          </div>
        </div>

        <div className="flex justify-between items-center">
          <div className="text-[rgba(106,103,105,1)] dark:text-[rgba(156,163,175,1)] text-base">{t("token.userUnclaimedRewards")}</div>
          <div className="text-[rgba(16,185,129,1)] text-base">
            {gate === "unavailable" ? "—" : formatDecimalString(holding?.total_accrued_reward, 6)}
          </div>
        </div>
      </div>

      <ReasonModal
        open={open}
        danger
        title={t("token.adjustTitle", { ticker })}
        confirmLabel={confirmLabel}
        description={t("token.adjustWarning")}
        submitting={submitting}
        error={error}
        canSubmit={!amountInvalid}
        onCancel={() => setOpen(false)}
        onConfirm={submit}
      >
        <div>
          <label className="mb-1 block text-sm font-medium">{t("token.directionLabel")}</label>
          <div className="flex gap-2">
            {(["add", "remove"] as const).map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDirection(d)}
                className={clsx(
                  "flex-1 rounded-lg border px-3 py-2 text-sm transition-colors",
                  direction === d
                    ? d === "remove"
                      ? "border-red-500 bg-red-500/10 text-red-400"
                      : "border-emerald-500 bg-emerald-500/10 text-emerald-400"
                    : darkMode
                    ? "border-[rgba(255,255,255,0.1)] text-gray-400"
                    : "border-gray-300 text-gray-500"
                )}
              >
                {d === "add" ? t("token.addTokens") : t("token.removeTokens")}
              </button>
            ))}
          </div>
        </div>

        <TokenField
          label={direction === "remove" ? t("token.amountToRemove", { ticker }) : t("token.amountToAdd", { ticker })}
          value={amount}
          onChange={setAmount}
          invalid={amountInvalid && amount.trim() !== ""}
          hint={amountHint}
        />

        <p className={darkMode ? "text-xs text-gray-400" : "text-xs text-gray-500"}>
          {t("token.adjustFootnote")}
        </p>
      </ReasonModal>
    </>
  )
}
